import { getItemUid } from 'entities/CatalogItem/lib/getItemUid';
import { getCustomListCatalogItem } from 'entities/CustomList';

import type { MappedCatalogItem } from 'entities/CatalogItem/model/types/CatalogItemSchema';
import type { CustomListItem } from 'entities/CustomList';
import type { CatalogIdentity } from 'shared/lib/contexts/CatalogIdentityContext';

export interface ListOrderQuantity {
    targetQuantity: number;
    qty: number;
}

export interface ListOrderCoverage {
    allItemsOnOrder: boolean;
    /** Complete copies of the list on the order, limited by the item with the fewest copies. */
    listMultiple: number;
}

export const emptyListOrderCoverage = (): ListOrderCoverage => ({
    allItemsOnOrder: false,
    listMultiple: 0,
});

export const listItemTargetQuantity = (listItem: CustomListItem): number =>
    listItem.targetQuantity || 1;

export const getListOrderCoverageFromQuantities = (
    entries: ListOrderQuantity[],
): ListOrderCoverage => {
    if (entries.length === 0) {
        return emptyListOrderCoverage();
    }

    const listMultiple = Math.min(
        ...entries.map(({ qty, targetQuantity }) =>
            Math.floor(qty / (targetQuantity || 1)),
        ),
    );

    return {
        allItemsOnOrder: listMultiple >= 1,
        listMultiple,
    };
};

export const getListOrderCoverage = (
    listItems: CustomListItem[],
    selectedItems: Record<string, Partial<MappedCatalogItem>> | undefined,
    identity: CatalogIdentity,
): ListOrderCoverage => {
    const entries = listItems.flatMap((listItem) => {
        const catalogItem = getCustomListCatalogItem(listItem);
        if (!catalogItem) {
            return [];
        }

        const selectionKey = getItemUid(catalogItem, identity);
        return [
            {
                targetQuantity: listItemTargetQuantity(listItem),
                qty: selectedItems?.[selectionKey]?.qty ?? 0,
            },
        ];
    });

    return getListOrderCoverageFromQuantities(entries);
};
